import React, { useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import DOMPurify from 'isomorphic-dompurify';
// import mermaid from 'mermaid'; // COMMENTED OUT: Replaced with Three.js 2D implementations
import 'katex/dist/katex.min.css';
import '../styles/visuals.css';
import { VisualRenderer } from './visuals/VisualRenderer';

/**
 * Configure DOMPurify for safe HTML sanitization
 * Allows safe HTML tags while blocking potentially dangerous content
 */
const configureDOMPurify = () => {
  // Configure allowed tags and attributes
  const config = {
    // Allow common formatting tags
    ALLOWED_TAGS: [
      // Text formatting
      'p', 'br', 'span', 'div', 'b', 'i', 'em', 'strong', 'u', 's',
      'mark', 'small', 'sub', 'sup', 'code', 'pre',
      // Lists
      'ul', 'ol', 'li',
      // Tables
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      // Links (carefully controlled)
      'a',
      // Headers
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      // Quotes and sections
      'blockquote', 'hr',
      // Images (for diagrams/charts if needed)
      'img',
      // KaTeX math rendering elements
      'math', 'semantics', 'mrow', 'msup', 'mi', 'mn', 'mo', 'mfrac',
      'msqrt', 'mroot', 'mtext', 'mspace', 'menclose', 'annotation'
    ],

    // Allowed attributes for specific tags
    ALLOWED_ATTR: [
      'class', 'id', 'style', 'href', 'target', 'rel',
      'src', 'alt', 'width', 'height',
      // KaTeX attributes
      'xmlns', 'aria-hidden'
    ],

    // Protocols allowed for links and images
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,

    // Keep relative URLs
    ALLOW_DATA_ATTR: false, // Block data-* attributes

    // Additional security settings
    ALLOW_UNKNOWN_PROTOCOLS: false,
    SAFE_FOR_TEMPLATES: true,
    WHOLE_DOCUMENT: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    FORCE_BODY: false
  };

  return config;
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param {string} dirtyHTML - Potentially unsafe HTML content
 * @returns {string} - Sanitized HTML
 */
const sanitizeHTML = (dirtyHTML) => {
  if (!dirtyHTML || typeof dirtyHTML !== 'string') {
    return '';
  }

  const config = configureDOMPurify();

  // Sanitize the HTML
  const clean = DOMPurify.sanitize(dirtyHTML, config);

  // Additional post-processing: remove any script-like patterns that might have slipped through
  let safeCleaned = clean
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '') // Remove inline event handlers
    .replace(/javascript:/gi, ''); // Remove javascript: protocol

  return safeCleaned;
};

// COMMENTED OUT: Mermaid component - replaced with Three.js 2D implementations
// const MermaidDiagram = ({ chart }) => {
//   const ref = useRef(null);
//   const diagramId = useRef(`mermaid-${Date.now()}-${Math.floor(Math.random() * 1000)}`);

//   useEffect(() => {
//     if (ref.current && chart) {
//       // Initialize Mermaid
//       mermaid.initialize({
//         startOnLoad: false,
//         theme: 'default',
//         securityLevel: 'loose',
//         flowchart: {
//           useMaxWidth: true,
//           htmlLabels: true
//         }
//       });

//       const renderChart = async () => {
//         try {
//           // Clear previous content
//           ref.current.innerHTML = '';

//           // Validate chart content
//           if (!chart.trim()) {
//             throw new Error('Empty chart content');
//           }

//           // Use mermaid.render with proper ID
//           const { svg } = await mermaid.render(diagramId.current, chart.trim());
//           ref.current.innerHTML = svg;
//         } catch (error) {
//           ref.current.innerHTML = `
//             <div class="mermaid-error">
//               <strong>Error rendering diagram:</strong> ${error.message}
//               <br><small>Please check your Mermaid syntax</small>
//             </div>
//           `;
//         }
//       };

//       renderChart();
//     }
//   }, [chart]);

//   return <div ref={ref} className="mermaid-container" />;
// };

// Custom renderer for code blocks to handle visuals and Mermaid
const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  // List of visual types that should be rendered with VisualRenderer
  // NOTE: 'mermaid' removed - now using Three.js 2D for diagrams/flowcharts
  const visualTypes = ['chart', 'molecule', 'map', 'music', 'circuit', 'diagram', 'graph', 'threejs'];

  if (!inline) {
    // Handle all visual types with VisualRenderer
    if (visualTypes.includes(language)) {
      return (
        <VisualRenderer
          language={language}
          code={String(children).trim()}
        />
      );
    }
  }

  // Regular code blocks
  return (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

// Main ContentRenderer component
const ContentRenderer = ({ content, sources, visualCode }) => {
  // Sanitize content once when it changes using useMemo for performance
  const sanitizedContent = useMemo(() => {
    if (!content || typeof content !== 'string') {
      return '';
    }

    // Sanitize HTML content to prevent XSS attacks
    // Note: This sanitizes only HTML, markdown syntax is preserved
    let processedContent = sanitizeHTML(content);

    // If visual_code exists (from database), inject it into content
    // This allows visuals generated separately to render within the section content
    if (visualCode && typeof visualCode === 'string') {
      // Simple approach: Append visual at the end of content
      // The visual will render inline as part of the markdown
      processedContent = processedContent + '\n\n' + visualCode;
    }

    return processedContent;
  }, [content, visualCode]);

  if (!content) {
    return (
      <div className="content-placeholder">
        <p>No content to display. This is where your course content will appear.</p>
        <div className="content-examples">
          <h3>Supported Formats:</h3>
          <ul>
            <li><strong>Markdown:</strong> Standard markdown formatting</li>
            <li><strong>LaTeX Math:</strong> Inline math with $...$ or block math with $$...$$</li>
            <li><strong>Three.js Diagrams:</strong> 2D flowcharts, processes, hierarchies</li>
            <li><strong>Interactive Simulations:</strong> Physics, biology, data structures</li>
            <li><strong>Educational Games:</strong> Interactive learning experiences</li>
            <li><strong>Charts:</strong> Bar, line, and pie charts</li>
            <li><strong>3D Molecules:</strong> Interactive molecular structures</li>
            <li><strong>Maps:</strong> Geographic locations and markers</li>
            <li><strong>Music Notation:</strong> Musical scores and notes</li>
            <li><strong>Circuits:</strong> Electronic circuit diagrams</li>
            <li><strong>Labeled Diagrams:</strong> Anatomy, biology, systems</li>
            <li><strong>Mathematical Graphs:</strong> Function plots</li>
          </ul>
        </div>
      </div>
    );
  }

  // Format accessed date for display
  const formatDate = (isoDate) => {
    try {
      const date = new Date(isoDate);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return isoDate;
    }
  };

  return (
    <div className="content-renderer">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex, rehypeRaw]}
        components={{
          code: CodeBlock,
        }}
      >
        {sanitizedContent}
      </ReactMarkdown>

      {/* Display sources as footnotes if available */}
      {sources && sources.length > 0 && (
        <div className="sources-footnotes">
          <hr className="sources-divider" />
          <h4 className="sources-heading">Sources</h4>
          <ol className="sources-list">
            {sources.map((source, index) => (
              <li key={index} className="source-item">
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="source-link"
                >
                  {source.title}
                </a>
                {source.description && (
                  <span className="source-description"> - {source.description}</span>
                )}
                <span className="source-date"> (Accessed: {formatDate(source.accessed_date)})</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default ContentRenderer; 